import { uniqueId } from 'lodash';
import { uniqBy } from 'ramda';
import { MessageType } from '../../../../../types/chat.types';
import { isVideoCallMessageType } from '../../../../chatSocket/reducer/chatSocketReducer.utils';
import { isMainChat } from '../useChats';
import {
  ChatListActions,
  ChatNotificationState,
  ChatRoomsListState,
  ChatState,
  GroupedChatRoomsState,
} from './chatListReducer.types';
import { findChatByIdInGroup, updateChatRoomsWithNewChat } from './chatListReducer.utils';

export const chatListInitialState: ChatRoomsListState = {
  page: 0,
  chatRooms: [],
  totalItems: 0,
  hasMore: true,
  error: undefined,
  isLoading: false,
  notifications: new Map(),
};

export const chatListReducer = (
  state: ChatRoomsListState,
  action: ChatListActions,
): ChatRoomsListState => {
  switch (action.type) {
    case 'RESET_STATE':
      return chatListInitialState;

    case 'FETCHING_MORE':
      return {
        ...state,
        isLoading: true,
      };

    case 'ERROR_FETCHING_GROUPS': {
      return {
        ...state,
        isLoading: false,
        error: true,
      };
    }

    case 'FETCH_MORE_RESULT': {
      const { groupCount, groupedChats, chatsToGroups } = action.payload;
      // TODO: QUAL AS CHANCES DE EU DUPLICAR OS GRUPOS AQUI? COMO EU POSSO EVITAR ISSO? NA TEORIA TALVEZ TENHA COMO POIS AO MESMO TEMPO QUE EU ADICIONO UM NOVO GRUPO VIA WEBSOCKET, O ÚLTIMO DA PÁGINA CARREGADA SERÁ O PRIMEIRO DA PRÓXIMA LISTA, TALVEZ TENHAMOS QUE ALTERAR A FORMA DE LOAD PARA UM SKIP E NÃO PAGE

      const groupsWithChats: GroupedChatRoomsState[] = groupedChats.map((group) => {
        const groupChat = chatsToGroups?.find((chat) =>
          group.chatIdsPresentInThisGrouping.includes(chat.id),
        );

        if (!groupChat) {
          return {
            ...group,
            metaId: uniqueId(),
            chats: [],
          };
        }

        const groupChatAsState: ChatState = {
          ...groupChat,
          wasRemoved: false,
        };

        const newChatIsMain = isMainChat(groupChatAsState);
        const chatToAdd = newChatIsMain
          ? { mainChat: groupChatAsState, chats: [] }
          : { chats: [groupChatAsState] };

        return {
          ...group,
          metaId: uniqueId(),
          ...chatToAdd,
        };
      });

      const newItems = [...state.chatRooms, ...groupsWithChats];
      return {
        ...state,
        isLoading: false,
        chatRooms: newItems,
        totalItems: groupCount,
        hasMore: newItems.length < groupCount,
        page: state.page + 1,
      };
    }

    case 'ADD_NEW_CHAT': {
      const { chat: newChat, groupOfChat: newGroupOfChat } = action.payload;

      const loadedGroupOfChat = state.chatRooms.find((group) =>
        group.chatIdsPresentInThisGrouping.some((chatId) =>
          newGroupOfChat.chatIdsPresentInThisGrouping.includes(chatId),
        ),
      );

      const newChatState: ChatState = {
        ...newChat,
        wasRemoved: false,
      };

      const newChatRoomState = updateChatRoomsWithNewChat({
        newChat: newChatState,
        chatRooms: state.chatRooms,
        groupOfChat: loadedGroupOfChat,
        newGroupOfChat: loadedGroupOfChat ? undefined : newGroupOfChat,
      });

      return {
        ...state,
        chatRooms: newChatRoomState,
        totalItems: newGroupOfChat ? state.totalItems + 1 : state.totalItems,
      };
    }

    case 'UPDATE_CHAT_TO_TOP': {
      const { chat, websocketMessages } = action.payload;

      const groupOfChat = state.chatRooms.find((group) => findChatByIdInGroup(group, chat.id));

      if (!groupOfChat) {
        return state;
      }

      const chatFound = findChatByIdInGroup(groupOfChat, chat.id);

      if (!chatFound) {
        return state;
      }
      const websocketMessage = websocketMessages.findLast((message) => message.chatId === chat.id);
      const messageAsVideoCallMessage =
        websocketMessage && isVideoCallMessageType(websocketMessage) ? websocketMessage : undefined;
      const inProgressVideoCall = chat.inProgressVideoCall || messageAsVideoCallMessage?.data;

      const lastMessage = websocketMessage
        ? {
            body: websocketMessage.body,
            type: websocketMessage.type,
            updatedAt: websocketMessage.updatedAt,
            authorName: websocketMessage.user.name,
            authorEmail: websocketMessage.user.email,
          }
        : chatFound.lastMessage;
      const newChatState: ChatState = {
        ...chat,
        lastMessage,
        inProgressVideoCall,
      };

      const newChatRoomState = updateChatRoomsWithNewChat({
        groupOfChat,
        newChat: newChatState,
        chatRooms: state.chatRooms,
      });

      return {
        ...state,
        chatRooms: newChatRoomState,
      };
    }

    case 'UPDATE_CHAT_WITH_NOTIFICATION': {
      const { newNotification, videoCallStarted } = action.payload;

      const groupOfChat = state.chatRooms.find((group) =>
        findChatByIdInGroup(group, newNotification.chatId),
      );

      if (!groupOfChat) {
        return state;
      }

      const chatFound = findChatByIdInGroup(groupOfChat, newNotification.chatId);

      if (!chatFound) {
        return state;
      }

      const newChatState: ChatState = {
        ...chatFound,
        lastMessage: {
          type: newNotification.type,
          body: newNotification.message,
          updatedAt: newNotification.updatedAt,
          authorName: newNotification.authorName,
          authorEmail: newNotification.authorEmail,
        },
        inProgressVideoCall: videoCallStarted || chatFound.inProgressVideoCall,
      };

      const newChatRoomsState = updateChatRoomsWithNewChat({
        groupOfChat,
        newChat: newChatState,
        chatRooms: state.chatRooms,
      });

      state.notifications.set(newNotification.chatId, {
        lastViewedChatMessageId:
          state.notifications.get(newNotification.chatId)?.lastViewedChatMessageId || undefined,
        unreadMessagesCount: state.notifications.get(newNotification.chatId)
          ? state.notifications.get(newNotification.chatId)!.unreadMessagesCount + 1
          : 1,
      });

      return {
        ...state,
        chatRooms: newChatRoomsState,
        notifications: new Map(state.notifications),
      };
    }

    case 'ADD_VIDEO_CALL_IN_ROOM': {
      const { chatId, videoCall, user } = action.payload;

      const groupOfChat = state.chatRooms.find((group) => findChatByIdInGroup(group, chatId));

      if (!groupOfChat) {
        return state;
      }

      const chatFound = findChatByIdInGroup(groupOfChat, chatId);

      if (!chatFound) {
        return state;
      }

      const chatRoomWithVideoCall: ChatState = {
        ...chatFound,
        lastMessage: {
          body: 'video call',
          authorName: user.name,
          authorEmail: user.email,
          type: MessageType.VIDEO_CALL,
          updatedAt: videoCall.startDate,
        },
        inProgressVideoCall: videoCall,
      };

      const newChatRoomsState = updateChatRoomsWithNewChat({
        groupOfChat,
        newChat: chatRoomWithVideoCall,
        chatRooms: state.chatRooms,
      });
      return {
        ...state,
        chatRooms: newChatRoomsState,
      };
    }

    case 'OVERWRITE_NOTIFICATIONS': {
      const { notifications } = action.payload;

      const chatNotificationState: ChatNotificationState = new Map();

      notifications.forEach((notification) => {
        chatNotificationState.set(notification.chatId, {
          lastViewedChatMessageId: notification.lastViewedChatMessageId,
          unreadMessagesCount: notification.unreadMessagesCount,
        });
      });

      return {
        ...state,
        notifications: chatNotificationState,
      };
    }

    case 'UPDATE_LAST_VIEWED_MESSAGE_IN_CHAT': {
      const { chatId, lastViewedMessageId } = action.payload;

      state.notifications.set(chatId, {
        unreadMessagesCount: 0,
        lastViewedChatMessageId: lastViewedMessageId,
      });

      return {
        ...state,
        notifications: new Map(state.notifications),
      };
    }

    case 'ADD_CHATS_TO_GROUPS': {
      const { chats, groupMetaId } = action.payload;

      const newGroupChats = chats.map((chat) => ({ ...chat, wasRemoved: false }));

      const newChatRooms = state.chatRooms.map((chatRoom) => {
        if (chatRoom.metaId !== groupMetaId) {
          return chatRoom;
        }

        const newChats = chatRoom.mainChat
          ? [...chatRoom.chats, ...newGroupChats]
          : [...chatRoom.chats, ...newGroupChats].filter((chat) => !isMainChat(chat));
        const mainChat = chatRoom.mainChat || newGroupChats.find(isMainChat);

        const chatsWithoutDuplicates = uniqBy((chat) => chat.id, newChats);

        return {
          ...chatRoom,
          mainChat,
          chats: chatsWithoutDuplicates,
        };
      });

      return {
        ...state,
        chatRooms: newChatRooms,
      };
    }

    case 'UPDATE_CHAT_INFO': {
      const { chat: chatWithNewInfo } = action.payload;

      const newChatRooms = state.chatRooms.map((chatRoom) => {
        if (isMainChat(chatWithNewInfo) && chatRoom.mainChat?.id === chatWithNewInfo.id) {
          return {
            ...chatRoom,
            mainChat: { ...chatWithNewInfo, wasRemoved: false },
          };
        } else {
          const newChats = chatRoom.chats.map((chat) => {
            if (chat.id !== chatWithNewInfo.id) {
              return chat;
            }

            return {
              ...chat,
              ...chatWithNewInfo,
            };
          });

          return {
            ...chatRoom,
            chats: newChats,
          };
        }
      });

      return {
        ...state,
        chatRooms: newChatRooms,
      };
    }

    default:
      throw new Error('Ação do reducer não encontrada');
  }
};
